import { DobDualListBoxOption } from '@dedalusdiit/dob-components';
import { getTitleForWorklistProperty } from './WorklistProperties';

interface WorklistDualListItem<TProperty extends string> {
    id: TProperty;
    label: string;
}

interface WorklistViewParam<TProperty extends string> {
    ID: TProperty;
    Visible: boolean;
}

type WorklistPropertyWithTitle = Parameters<typeof getTitleForWorklistProperty>[0];

function toSortedDualListOptions<TProperty extends WorklistPropertyWithTitle>(
    viewParams: WorklistViewParam<TProperty>[],
): DobDualListBoxOption<WorklistDualListItem<TProperty>>[] {
    return viewParams
        .map((param) => ({
            item: {
                id: param.ID,
                label: getTitleForWorklistProperty(param.ID),
            },
        }))
        .sort((a, b) => a.item.label.localeCompare(b.item.label));
}

function toActiveDualListItems<TProperty extends WorklistPropertyWithTitle>(
    viewParams: WorklistViewParam<TProperty>[],
): DobDualListBoxOption<WorklistDualListItem<TProperty>>[] {
    return viewParams
        .filter((param) => param.Visible)
        .map((param) => ({
            item: {
                id: param.ID,
                label: getTitleForWorklistProperty(param.ID),
            },
        }));
}

function applyDualListSelectionToViewParams<TProperty extends WorklistPropertyWithTitle>(
    allOptions: DobDualListBoxOption<WorklistDualListItem<TProperty>>[],
    activeOptions: DobDualListBoxOption<WorklistDualListItem<TProperty>>[],
    previousActiveItems: DobDualListBoxOption<WorklistDualListItem<TProperty>>[],
): WorklistViewParam<TProperty>[] {
    const activeIds = new Set(activeOptions.map((option) => option.item.id));

    const previouslyActiveIds = previousActiveItems.map((option) => option.item.id);
    const previouslyActiveIdSet = new Set(previouslyActiveIds);

    const isSameActiveSet
        = activeIds.size === previouslyActiveIdSet.size
            && previouslyActiveIds.every((id) => activeIds.has(id));

    let orderedActiveIds: TProperty[];
    if (isSameActiveSet) {
        orderedActiveIds = activeOptions.map((option) => option.item.id);
    }
    else {
        const retainedActiveIds = previouslyActiveIds.filter((id) => activeIds.has(id));
        const newlyActiveIds = activeOptions
            .map((option) => option.item.id)
            .filter((id) => !previouslyActiveIdSet.has(id));
        orderedActiveIds = [...retainedActiveIds, ...newlyActiveIds];
    }

    const activeViewParams: WorklistViewParam<TProperty>[] = orderedActiveIds.map((id) => ({
        ID: id,
        Visible: true,
    }));

    const inactiveViewParams: WorklistViewParam<TProperty>[] = allOptions
        .filter((option) => !activeIds.has(option.item.id))
        .map((option) => ({
            ID: option.item.id,
            Visible: false,
        }));

    return [...activeViewParams, ...inactiveViewParams];
}

export {
    applyDualListSelectionToViewParams,
    toActiveDualListItems,
    toSortedDualListOptions,
    type WorklistDualListItem,
};
